import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { 
  ArrowRight, 
  Star, 
  Users, 
  Calendar, 
  Shield, 
  Building2,
  ChevronRight,
  CheckCircle2
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { ExpertCard } from "@/components/ExpertCard";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import { Footer } from "@/components/Footer";
import axios from "axios";
import { API } from "@/App";

export default function LandingPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [experts, setExperts] = useState([]);

  useEffect(() => {
    const fetchExperts = async () => {
      try {
        const response = await axios.get(`${API}/experts?limit=6`);
        setExperts(response.data);
      } catch (error) {
        console.error("Error fetching experts:", error);
      }
    };
    fetchExperts();
  }, []);

  const stats = [
    { value: "500+", label: t('landing.stats.experts') },
    { value: "1,000+", label: t('landing.stats.sessions') },
    { value: "4.9", label: t('landing.stats.rating') },
    { value: "50+", label: t('landing.stats.countries') },
  ];

  const categories = [
    { name: "Fundraising", icon: "💰", count: 89 },
    { name: "Go-to-Market", icon: "🚀", count: 124 },
    { name: "Product Strategy", icon: "📦", count: 76 },
    { name: "Growth & Marketing", icon: "📈", count: 98 },
    { name: "Technical Advisory", icon: "⚡", count: 67 },
    { name: "M&A & Exits", icon: "🤝", count: 45 },
  ];

  const testimonials = [
    {
      quote: "The session completely transformed our pitch deck. We closed our Series A two weeks later.",
      author: "Founder A",
      role: "Tech Startup",
      initials: "FA"
    },
    {
      quote: "Access to this caliber of expertise used to be impossible. ExpertBase changed everything for our startup.",
      author: "Founder B",
      role: "E-Commerce Company",
      initials: "FB"
    },
    {
      quote: "One hour with an ExpertBase advisor saved us months of trial and error. Worth every penny.",
      author: "Founder C",
      role: "SaaS Startup",
      initials: "FC"
    }
  ];

  return (
    <div className="min-h-screen bg-obsidian">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center hero-gradient noise-overlay overflow-hidden pt-20 sm:pt-24 lg:pt-0">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 left-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-blue-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-64 sm:w-96 h-64 sm:h-96 bg-gold-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-12 sm:py-20 lg:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Content */}
            <motion.div 
              className="lg:col-span-7 space-y-6 sm:space-y-8"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Coming Soon Banner */}
              <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-gold-500/20 border border-gold-500/40 rounded-full">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-gold-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-gold-500"></span>
                </span>
                <span className="text-gold-500 font-medium text-xs sm:text-sm tracking-wide">{t('landing.hero.comingSoon', 'COMING SOON')}</span>
              </div>
              
              <h1 className="font-display text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-medium tracking-tight leading-[1.1]">
                {t('landing.hero.title')}
              </h1>
              
              <p className="text-base sm:text-lg lg:text-xl text-slate-400 max-w-xl leading-relaxed">
                {t('landing.hero.subtitle')}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <Button 
                  onClick={() => navigate("/experts")}
                  className="bg-gold-500 hover:bg-gold-400 text-black font-semibold px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-sm btn-shine w-full sm:w-auto"
                  data-testid="browse-experts-btn"
                >
                  {t('landing.hero.browseExperts')}
                  <ArrowRight className="ml-2 w-4 sm:w-5 h-4 sm:h-5" />
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => navigate("/enterprise")}
                  className="border-slate-700 hover:border-gold-500 hover:text-gold-500 px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-sm w-full sm:w-auto"
                  data-testid="enterprise-btn"
                >
                  <Building2 className="mr-2 w-4 sm:w-5 h-4 sm:h-5" />
                  {t('nav.enterprise')}
                </Button>
              </div>
              
              {/* Trust Badges */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-8 pt-6 sm:pt-8 border-t border-slate-800/50">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 sm:w-5 h-4 sm:h-5 text-green-500" />
                  <span className="text-xs sm:text-sm text-slate-400">{t('landing.hero.vettedExperts', 'Vetted Experts')}</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 sm:w-5 h-4 sm:h-5 text-green-500" />
                  <span className="text-xs sm:text-sm text-slate-400">{t('landing.hero.moneyBack', 'Money-back Guarantee')}</span>
                </div>
              </div>
            </motion.div>
            
            {/* Right Content - Featured Expert Cards */}
            <motion.div 
              className="lg:col-span-5 relative mt-8 lg:mt-0"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative mx-auto max-w-md lg:max-w-none">
                {/* Main Card */}
                <div className="bg-charcoal border border-slate-800 p-4 sm:p-6 rounded-sm">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className="w-14 h-14 sm:w-20 sm:h-20 bg-gold-500/20 rounded-sm flex items-center justify-center shrink-0">
                      <span className="font-display text-xl sm:text-2xl text-gold-500">EA</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-[10px] sm:text-xs bg-gold-500/20 text-gold-500 px-2 py-0.5 rounded">{t('landing.hero.topExpert', 'TOP EXPERT')}</span>
                      </div>
                      <h3 className="font-display text-lg sm:text-xl">Expert A</h3>
                      <p className="text-xs sm:text-sm text-slate-400 truncate">Former VP at Fortune 500, 3x Founder</p>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 mt-2 sm:mt-3">
                        <div className="flex items-center gap-1">
                          <Star className="w-3 h-3 sm:w-4 sm:h-4 fill-gold-500 text-gold-500" />
                          <span className="text-xs sm:text-sm">4.9</span>
                        </div>
                        <span className="text-slate-600 hidden sm:inline">|</span>
                        <span className="text-xs sm:text-sm text-slate-400">127 {t('experts.card.sessions')}</span>
                        <span className="text-slate-600 hidden sm:inline">|</span>
                        <span className="text-xs sm:text-sm font-semibold text-gold-500">$500{t('experts.card.perHour')}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-3 sm:mt-4">
                    <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-slate-800 text-[10px] sm:text-xs text-slate-300 rounded-full">Fundraising</span>
                    <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-slate-800 text-[10px] sm:text-xs text-slate-300 rounded-full">FinTech</span>
                    <span className="px-2 sm:px-3 py-0.5 sm:py-1 bg-slate-800 text-[10px] sm:text-xs text-slate-300 rounded-full">GTM</span>
                  </div>
                </div>
                
                {/* Floating Cards - Hidden on very small screens, repositioned for tablets */}
                <div className="absolute -top-3 right-0 sm:-top-4 sm:-right-4 bg-charcoal border border-slate-800 px-3 sm:px-4 py-2 sm:py-3 rounded-sm shadow-xl">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full pulse" />
                    <span className="text-xs sm:text-sm text-slate-300">{t('landing.hero.expertsOnline', '23 experts online')}</span>
                  </div>
                </div>
                
                <div className="absolute -bottom-3 left-0 sm:-bottom-4 sm:-left-4 bg-charcoal border border-slate-800 px-3 sm:px-4 py-2 sm:py-3 rounded-sm shadow-xl">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-gold-500" />
                    <div>
                      <p className="text-xs sm:text-sm font-medium">{t('landing.hero.bookIn60')}</p>
                      <p className="text-[10px] sm:text-xs text-slate-500">{t('landing.hero.instantScheduling')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 sm:py-16 border-y border-slate-800/50 bg-charcoal/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            {stats.map((stat, index) => (
              <motion.div 
                key={index}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <p className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl text-gold-500 mb-1 sm:mb-2">{stat.value}</p>
                <p className="text-xs sm:text-sm text-slate-400">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 sm:py-20 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div 
            className="mb-10 sm:mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-gold-500 mb-3 sm:mb-4">{t('landing.categories.tagline')}</p>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl mb-4 sm:mb-6">
              {t('landing.categories.title')}<br className="hidden sm:block" /><span className="sm:hidden"> </span>{t('landing.categories.subtitle')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {categories.map((category, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                viewport={{ once: true }}
              >
                <Link 
                  to={`/experts?expertise=${encodeURIComponent(category.name)}`}
                  className="block group p-4 sm:p-6 bg-charcoal border border-slate-800 hover:border-gold-500/50 rounded-sm transition-all duration-300"
                  data-testid={`category-${category.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 sm:gap-4">
                      <span className="text-2xl sm:text-3xl">{category.icon}</span>
                      <div>
                        <h3 className="font-medium text-base sm:text-lg group-hover:text-gold-500 transition-colors">{category.name}</h3>
                        <p className="text-xs sm:text-sm text-slate-500">{category.count} {t('landing.categories.expertsCount')}</p>
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-slate-600 group-hover:text-gold-500 group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Experts */}
      <section className="py-16 sm:py-20 md:py-24 lg:py-32 bg-charcoal/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div 
            className="flex flex-col sm:flex-row sm:items-end sm:justify-between mb-10 sm:mb-16 gap-4 sm:gap-6"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div>
              <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-gold-500 mb-3 sm:mb-4">{t('landing.featured.tagline')}</p>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl">
                {t('landing.featured.title')}
              </h2>
            </div>
            <Link 
              to="/experts" 
              className="flex items-center gap-2 text-gold-500 hover:text-gold-400 transition-colors text-sm sm:text-base"
              data-testid="view-all-experts"
            >
              {t('landing.featured.viewAll')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {experts.slice(0, 6).map((expert, index) => (
              <motion.div
                key={expert.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <ExpertCard expert={expert} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-20 md:py-24 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div 
            className="text-center mb-12 sm:mb-16 lg:mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-gold-500 mb-3 sm:mb-4">{t('landing.howItWorks.tagline')}</p>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl">
              {t('landing.howItWorks.title')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {[
              {
                step: "01",
                title: t('landing.howItWorks.step1.title'),
                description: t('landing.howItWorks.step1.description')
              },
              {
                step: "02",
                title: t('landing.howItWorks.step2.title'),
                description: t('landing.howItWorks.step2.description')
              },
              {
                step: "03",
                title: t('landing.howItWorks.step3.title'),
                description: t('landing.howItWorks.step3.description')
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                className="relative p-5 sm:p-6 lg:p-8 bg-charcoal border border-slate-800 rounded-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <span className="font-display text-4xl sm:text-5xl lg:text-6xl text-gold-500/20">{item.step}</span>
                <h3 className="font-display text-xl sm:text-2xl mt-3 sm:mt-4 mb-2 sm:mb-3">{item.title}</h3>
                <p className="text-sm sm:text-base text-slate-400 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 md:py-24 lg:py-32 bg-charcoal/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <motion.div 
            className="text-center mb-12 sm:mb-16 lg:mb-20"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-gold-500 mb-3 sm:mb-4">{t('landing.testimonials.tagline')}</p>
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl">
              {t('landing.testimonials.title')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="p-5 sm:p-6 lg:p-8 bg-obsidian border border-slate-800 rounded-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="flex gap-1 mb-4 sm:mb-6">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-3 h-3 sm:w-4 sm:h-4 fill-gold-500 text-gold-500" />
                  ))}
                </div>
                <p className="text-sm sm:text-base lg:text-lg text-slate-300 mb-4 sm:mb-6 leading-relaxed">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3 sm:gap-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gold-500/20 flex items-center justify-center shrink-0">
                    <span className="font-semibold text-gold-500 text-sm sm:text-base">{testimonial.initials}</span>
                  </div>
                  <div>
                    <p className="font-medium text-sm sm:text-base">{testimonial.author}</p>
                    <p className="text-xs sm:text-sm text-slate-500">{testimonial.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Enterprise CTA */}
      <section className="py-16 sm:py-20 md:py-24 lg:py-32 relative overflow-hidden">
        <div className="absolute inset-0 z-0 bg-charcoal">
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/90 to-obsidian/70" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-gold-500 mb-3 sm:mb-4">{t('landing.enterprise.tagline')}</p>
              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl mb-4 sm:mb-6">
                {t('landing.enterprise.title')}
              </h2>
              <p className="text-base sm:text-lg lg:text-xl text-slate-400 mb-6 sm:mb-8 leading-relaxed">
                {t('landing.enterprise.subtitle')}
              </p>
              <ul className="space-y-3 sm:space-y-4 mb-8 sm:mb-10">
                {[
                  t('landing.enterprise.features.1'),
                  t('landing.enterprise.features.2'),
                  t('landing.enterprise.features.3'),
                  t('landing.enterprise.features.4')
                ].map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-4 h-4 sm:w-5 sm:h-5 text-gold-500 flex-shrink-0" />
                    <span className="text-sm sm:text-base text-slate-300">{item}</span>
                  </li>
                ))}
              </ul>
              <Button 
                onClick={() => navigate("/enterprise")}
                className="bg-gold-500 hover:bg-gold-400 text-black font-semibold px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-sm w-full sm:w-auto"
                data-testid="enterprise-cta-btn"
              >
                {t('landing.enterprise.contactSales')}
                <ArrowRight className="ml-2 w-4 sm:w-5 h-4 sm:h-5" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 sm:py-20 md:py-24 lg:py-32 border-t border-slate-800/50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl mb-4 sm:mb-6">
              {t('landing.cta.title')}
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-slate-400 mb-8 sm:mb-10">
              {t('landing.cta.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Button 
                onClick={() => navigate("/experts")}
                className="bg-gold-500 hover:bg-gold-400 text-black font-semibold px-8 sm:px-10 py-5 sm:py-6 text-base sm:text-lg rounded-sm btn-shine"
                data-testid="final-cta-btn"
              >
                {t('landing.cta.findExpert')}
                <ArrowRight className="ml-2 w-4 sm:w-5 h-4 sm:h-5" />
              </Button>
              <Button 
                onClick={() => navigate("/become-expert")}
                variant="outline"
                className="border-slate-700 hover:border-gold-500 hover:text-gold-500 px-8 sm:px-10 py-5 sm:py-6 text-base sm:text-lg rounded-sm"
                data-testid="become-expert-btn"
              >
                {t('landing.cta.becomeExpert')}
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
